import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {of} from 'rxjs';
import {catchError, exhaustMap, map, mergeMap, tap, withLatestFrom} from 'rxjs/operators';
import * as GlobalMessageActions from '../../core/actions/global-message.actions';
import {GlobalMessage, GlobalMessageType} from '../../core/models/global-message';
import * as ProductActions from '../../products/actions/product.actions';
import * as fromProd from '../../products/reducers';
import * as AuthActions from '../../user/actions/user.actions';
import * as QuotesActions from '../actions/quotes.actions';
import {QuotesError} from '../model/quotes-error';
import {QuotesService} from '../services/quotes.service';

@Injectable()
export class QuotesEffects {

    logout$ = createEffect(() => this.actions$
        .pipe(
            ofType(AuthActions.logout),
            exhaustMap(() => {
                return of(QuotesActions.cleanUp());
            })
        ));

    load$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuotesActions.load),
            tap(() => console.log('Quotes effect load')),
            exhaustMap(() => {
                return this.service
                    .getQuotes()
                    .pipe(
                        map(quotes => QuotesActions.loadingSuccess({quotes})),
                        catchError(error => of(QuotesActions.loadingFailure({error: (<QuotesError>error).errorMessage})))
                    );
            })
        ));

    loadingSuccess$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuotesActions.loadingSuccess),
            tap(() => console.log('Quotes effect load')),
            withLatestFrom(this.store.pipe(select(fromProd.getProductEntities))),
            mergeMap(([action, productEntities]) => {
                const missingProducts: number[] = [];
                for (const quote of action.quotes) {
                    for (const quoteItem of quote.quoteItems) {
                        const productId = quoteItem.productId;
                        if (productEntities[productId] == null && !missingProducts.includes(productId)) {
                            missingProducts.push(productId);
                        }
                    }
                }
                if (missingProducts.length === 0) {
                    // There are only two possibilities, either an effect does dispatch a new action or the whole
                    // effect is set to 'dispatch: false' and won't dispatch a new action. Here we need a mix.
                    // We only want to dispatch a new action, if there are products to be loaded. Therefore we have
                    // to dispatch an action which won't be handled in the code base otherwise.
                    return of({type: 'NO_ACTION'});
                }
                return of(ProductActions.load({productIds: missingProducts}));
            })
        ));

    loadingFailure$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuotesActions.loadingFailure),
            tap(action => console.log('Quote effect loading failure', action)),
            mergeMap(action => {
                    const message: GlobalMessage = {
                        id: 'quotes-load-error',
                        type: GlobalMessageType.ERROR,
                        message: `The quotes couldn't be loaded: ${action.error}`
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));

    constructor(private actions$: Actions, private service: QuotesService, private store: Store<fromProd.State>) {
    }
}
